import { memo, useCallback, useEffect, useRef, useState, type JSX } from 'react'

import { FilterList } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import { SmepEvent } from '@nativewaves/nw-cloud-sdk/smep'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Table as ReactTableType, RowSelectionState } from '@tanstack/react-table'
import { VirtualItem } from '@tanstack/react-virtual'
import { omit, pick } from 'lodash-es'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Foldout } from '@shared/components/Foldout'
import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { Table, SelectionToolbar } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import {
  FilterPanel,
  NewDialog,
  RowActions,
  SelectionToolbarActions,
  SuccessPanelActions,
  useAsyncContentCaching,
  useColumns,
} from 'domains/Workspaces/Events'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { eventsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events'
import { eventDashboardRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events/Event'
import { eventQueryKeys } from 'services/queryKeys'

const OverviewRaw = (): JSX.Element => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const navigate = useNavigate({ from: eventsOverviewRoute.fullPath })
  const routeParams = eventsOverviewRoute.useParams()
  const { query } = eventsOverviewRoute.useSearch()
  const { workspaceId } = routeParams

  const tableInstance = useRef<ReactTableType<SmepEvent>>(undefined)
  const virtualizerInstanceRef = useRef<VirtualItem[]>(undefined)

  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const [loadingRows, setLoadingRows] = useState<string[]>([])
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)

  const { workspaceQuery, eventsQuery, setBatchGetEnabled } = useAsyncContentCaching({
    virtualItems: virtualizerInstanceRef.current,
    setLoadingRows,
  })

  const columns = useColumns()

  const handleSuccess = useCallback(
    (data: SmepEvent) => {
      enqueueSnackbar(`${t('domain:Workspace.Event.eventCreated', { name: data.name })}`, {
        description: `${t('domain:Workspace.Event.eventCreatedDescription')}`,
        variant: 'panel',
        persist: true,
        icon: 'success',
        Actions: (props) => <SuccessPanelActions {...props} {...routeParams} eventId={data.id} />,
      })
    },
    [enqueueSnackbar, routeParams, t],
  )

  useEffect(() => {
    if (eventsQuery.isSuccess) {
      tableInstance.current?.setRowSelection((old) =>
        Object.keys(old).reduce((acc, newItem) => {
          const newItemFound = eventsQuery.data.flat.find((task) => task.id === newItem)
          if (newItemFound) {
            return { ...acc, [newItem]: true }
          }
          return acc
        }, {} as RowSelectionState),
      )
    }
  }, [eventsQuery.data?.flat, eventsQuery.isSuccess])

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:Workspace.Event.searchEvents')}
          onSearch={({ search }) =>
            navigate({ search: (current) => (search.length ? { ...current, query: search } : omit(current, 'query')) })
          }
          defaultValue={query}
        />

        <IconButton size="medium" onClick={() => setFilterPanelOpen(!filterPanelOpen)}>
          <FilterList fontSize="small" />
        </IconButton>

        <Foldout open={filterPanelOpen} onClose={() => setFilterPanelOpen(false)} placement="end" hideBackdrop={false}>
          <FilterPanel />
        </Foldout>

        <BasicListOperation
          refreshAction={useCallback(() => {
            queryClient.cancelQueries({ queryKey: eventQueryKeys.batchGets() })
            setBatchGetEnabled(false)
            queryClient.invalidateQueries({
              queryKey: eventQueryKeys.list({ workspaceId }).queryKey,
            })
          }, [queryClient, setBatchGetEnabled, workspaceId])}
          createAction={useCallback(() => {
            setNewDialogOpen(true)
            tableInstance.current?.resetRowSelection()
          }, [])}
        />
      </ActionStrip>

      <Table
        getTableInstance={(instance) => (tableInstance.current = instance)}
        size={52}
        headSize={48}
        data={eventsQuery.data?.flat ?? []}
        columns={columns}
        isInitialLoadingGlobal={workspaceQuery.isLoading || eventsQuery.isLoading}
        isInitialLoadingByRowByID={loadingRows.reduce((acc, row) => ({ ...acc, [row]: [] }), {})}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={eventsOverviewRoute.fullPath}
            to={eventDashboardRoute.to}
            params={{ eventId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        excludeFieldsFromLinking={['_thumbnail', 'contentRepositoryId', 'alerts', 'id']}
        enableInfinity
        infinityProps={pick(eventsQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        enableVirtualization
        virtualizedProps={{
          getVirtualItems: (instance) => (virtualizerInstanceRef.current = instance),
        }}
        ActionMenuProps={(row) => ({ title: row.original.name, entity: t('entity:smep.event', { count: 1 }) })}
        RowActions={(props) => <RowActions {...props} />}
        renderSelectionToolbar={({ selectedRows }) => (
          <SelectionToolbar
            selectedRows={selectedRows}
            title={t('entity:smep.event', { count: selectedRows.length })}
            discardSelection={() => tableInstance.current?.toggleAllRowsSelected(false)}
            Actions={
              tableInstance.current && (
                <SelectionToolbarActions selectedRows={selectedRows} tableInstance={tableInstance.current} />
              )
            }
          />
        )}
        tableOptions={{
          getRowId: (avcTask) => avcTask.id,
        }}
      />

      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
        <NewDialog onSuccess={handleSuccess} />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
